import axios, { AxiosResponse, Method } from 'axios';

const RAW_API_BASE_URL = process.env.REACT_APP_BASE_URL ?? '';
const API_BASE_URL = RAW_API_BASE_URL.replace(/\/+$/, '');
const ensureLeadingSlash = (p: string) => (p.startsWith('/') ? p : `/${p}`);

// Final URL builder for axios calls
export const buildApiUrl = (path: string) => {
  const normalizedPath = ensureLeadingSlash(path);
  const url = API_BASE_URL ? `${API_BASE_URL}${normalizedPath}` : normalizedPath;
  return url;
};

// ------- file detection helpers
const isFile = (v: unknown): v is File | Blob =>
  (typeof File !== 'undefined' && v instanceof File) ||
  (typeof Blob !== 'undefined' && v instanceof Blob);

const isFileList = (v: unknown): v is FileList =>
  typeof FileList !== 'undefined' && v instanceof FileList;

const containsFile = (obj: any): boolean => {
  if (obj == null) return false;
  if (obj instanceof FormData) return true;
  if (isFile(obj) || isFileList(obj)) return true;
  if (Array.isArray(obj)) return obj.some(containsFile);
  if (typeof obj === 'object') return Object.values(obj).some(containsFile);
  return false;
};

const toFormData = (obj: any, fd = new FormData(), prefix?: string): FormData => {
  if (obj == null) return fd;

  const append = (key: string, value: any) => {
    if (isFileList(value)) {
      Array.from(value).forEach((f, idx) => fd.append(key, f, f.name ?? `file_${idx}`));
    } else if (isFile(value)) {
      fd.append(key, value as Blob);
    } else if (value instanceof Date) {
      fd.append(key, value.toISOString());
    } else if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      fd.append(key, String(value));
    } else if (Array.isArray(value)) {
      value.forEach((v, i) => toFormData(v, fd, `${key}[${i}]`));
    } else if (typeof value === 'object') {
      Object.entries(value).forEach(([k, v]) => append(prefix ? `${key}.${k}` : k, v));
    } else {
      fd.append(key, JSON.stringify(value));
    }
  };

  if (typeof obj === 'object' && !isFile(obj) && !isFileList(obj)) {
    Object.entries(obj).forEach(([k, v]) => append(prefix ? `${prefix}.${k}` : k, v));
  }
  return fd;
};

// note: don't hard-set content-type globally; decide per request
const jsonHeaders = (token?: string) => ({
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
  'Content-Type': 'application/json',
});

const multipartHeaders = (token?: string) => ({
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
  // let axios/browser set boundary automatically; specifying is optional
  'Content-Type': 'multipart/form-data',
});

export const AxiosCalls = {
  CallWithData: async <ResponseType, RequestType>(
    path: string,
    method: Method,
    data: RequestType,
    token?: string
  ): Promise<AxiosResponse<ResponseType>> => {
    const hasFiles = containsFile(data as any);
    const body = hasFiles
      ? data instanceof FormData
        ? data                // already FormData — use as-is
        : toFormData(data)    // plain object with files — convert
      : data;

    return axios({
      method,
      url: buildApiUrl(path),
      headers: hasFiles ? multipartHeaders(token) : jsonHeaders(token),
      data: body,
      withCredentials: true,
    });
  },

  CallWithParameters: async <ResponseType>(
    path: string,
    method: Method,
    token?: string
  ): Promise<AxiosResponse<ResponseType>> => {
    return axios({
      method,
      url: buildApiUrl(path),
      headers: jsonHeaders(token),
      withCredentials: true,
    });
  },

  Get: <ResponseType>(path: string, token?: string) =>
    AxiosCalls.CallWithParameters<ResponseType>(path, 'get', token),

  Post: <ResponseType, RequestType>(path: string, data: RequestType, token?: string) =>
    AxiosCalls.CallWithData<ResponseType, RequestType>(path, 'post', data, token),

  Put: <ResponseType, RequestType>(path: string, data: RequestType, token?: string) =>
    AxiosCalls.CallWithData<ResponseType, RequestType>(path, 'put', data, token),

  Delete: <ResponseType>(path: string, token?: string) =>
    AxiosCalls.CallWithParameters<ResponseType>(path, 'delete', token),

  DeleteWithData: <ResponseType, RequestType>(path: string, data: RequestType, token?: string) =>
    AxiosCalls.CallWithData<ResponseType, RequestType>(path, 'delete', data, token),
};
